import React, { useState } from 'react';
import { Form, Input, Select, DatePicker, InputNumber, Button, Upload, message, Row, Col, Spin, Space, Tabs, Card, Checkbox } from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { useAuth } from '../hooks/useAuth';
import { v4 as uuidv4 } from 'uuid';
import apiClient from '../services/api';
import type { Asset, Supplier, Employee } from '../types';
import dayjs from 'dayjs';
import SupplierModal from '../components/SupplierModal';
import LocationModal from '../components/LocationModal';
import AssetCategoryModal from '../components/AssetCategoryModal';
import EmployeeModal from '../components/EmployeeModal';
import axios from 'axios';
import { 
  MinusCircleOutlined, 
  PlusOutlined, 
  InfoCircleOutlined, 
  SettingOutlined, 
  ToolOutlined, 
  PaperClipOutlined,
  UserOutlined,
  ShopOutlined,
  AppstoreAddOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import AssetAccessoriesDraft, { DraftAccessory } from '../components/AssetAccessoriesDraft';
import debounce from 'lodash/debounce';
import { 
  processTechnicalSpecifications, 
  formatDateForAPI, 
  calculateTotalAccessoriesValue, 
  getTabMapForValidation,
  filterListByCompany 
} from '../utils/assetFormUtils';

// Props: truyền các danh sách từ component cha
interface LocationItem {
  id: number;
  name: string;
  company_id: number;
}
interface EmployeeItem extends Employee {
  company_id: number;
}
interface AssetCreateFormProps {
  companyList: { id: number; name: string; company_code?: string }[];
  categoryList: { id: number; name: string }[];
  locationList: LocationItem[];
  assetList: Asset[];
  supplierList: Supplier[];
  employeeList: EmployeeItem[];
  onSuccess: () => void;
  onCancel: () => void;
  onSupplierCreated?: () => void;
  onLocationCreated?: () => void;
  onCategoryCreated?: () => void;
  onEmployeeCreated?: () => void;
  initialValues?: any;
}

const AssetCreateForm: React.FC<AssetCreateFormProps> = ({ companyList, categoryList, locationList, assetList, supplierList, employeeList, onSuccess, onCancel, onSupplierCreated, onLocationCreated, onCategoryCreated, onEmployeeCreated, initialValues }) => {
  const { user, loading: authLoading } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [imageFiles, setImageFiles] = useState<UploadFile[]>([]);
  const [docFiles, setDocFiles] = useState<UploadFile[]>([]);
  const [supplierModalVisible, setSupplierModalVisible] = useState(false);
  const [currentSupplierList, setCurrentSupplierList] = useState<Supplier[]>(supplierList);
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [currentLocationList, setCurrentLocationList] = useState<LocationItem[]>(locationList);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [currentCategoryList, setCurrentCategoryList] = useState(categoryList);
  const [employeeModalVisible, setEmployeeModalVisible] = useState(false);
  const [currentEmployeeList, setCurrentEmployeeList] = useState<EmployeeItem[]>(employeeList);

  const [uploadedImages, setUploadedImages] = useState<any[]>([]); // Lưu thông tin file ảnh đã upload
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]); // Lưu thông tin file tài liệu đã upload
  const [accessoriesDraft, setAccessoriesDraft] = useState<DraftAccessory[]>([]);
  const [accessoriesKey, setAccessoriesKey] = useState(0);
  const [hasAccessories, setHasAccessories] = useState(false);
  const [assetCodeError, setAssetCodeError] = useState<string | null>(null);
  const [checkingAssetCode, setCheckingAssetCode] = useState(false);



  // Cập nhật giá trị tài sản khi accessories thay đổi
  const updateAssetValue = (accessories: DraftAccessory[]) => {
    const totalValue = calculateTotalAccessoriesValue(accessories);
    form.setFieldsValue({ purchase_cost: totalValue });
  };

  const [maintenanceTypes, setMaintenanceTypes] = useState<any[]>([]);
  const [selectedMaintenanceTypes, setSelectedMaintenanceTypes] = useState<number[]>([]);
  const [maintenanceCycles, setMaintenanceCycles] = useState<Record<string, number | undefined>>({});

  // Hàm tính ngày hết hạn bảo hành
  const calculateWarrantyExpiry = (purchaseDate: any, warrantyMonths: number) => {
    if (!purchaseDate || !warrantyMonths) return null;
    return dayjs(purchaseDate).add(warrantyMonths, 'month');
  };

  // Xử lý khi thay đổi ngày mua hoặc thời hạn bảo hành
  const handlePurchaseDateChange = (date: any) => {
    const warrantyMonths = form.getFieldValue('warranty_period_months');
    if (date && warrantyMonths) {
      const expiryDate = calculateWarrantyExpiry(date, warrantyMonths);
      form.setFieldsValue({ warranty_expiry_date: expiryDate });
    }
  };

  const handleWarrantyPeriodChange = (months: number | null) => {
    const purchaseDate = form.getFieldValue('purchase_date');
    if (purchaseDate && months && months > 0) {
      const expiryDate = calculateWarrantyExpiry(purchaseDate, months);
      form.setFieldsValue({ warranty_expiry_date: expiryDate });
    }
  };

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  // Xác định role
  const isSuperAdmin = user?.role === 'super_admin';
  const isCompanyAdminOrManager = user?.role === 'company_admin' || user?.role === 'manager';

  // Hàm sinh mã tài sản đúng chuẩn
  const generateAssetCode = async (companyId: number) => {
    const company = companyList.find(c => c.id === companyId);
    
    if (!company || !company.company_code) {
  
      return '';
    }
    
    try {
      // Gọi API backend để tạo mã tài sản tự động
  
      const response = await apiClient.get(`/assets/generate-asset-code/${companyId}`) as { 
        asset_code: string; 
        max_number: number; 
        next_number: number 
      };
      
      
      
      return response.asset_code;
    } catch (error) {

      // Fallback: sử dụng assetList nếu API fail
    const companyAssets = assetList.filter(a => a.company_id === companyId);
      
    
    let maxNumber = 0;
    companyAssets.forEach(a => {
      const match = a.asset_code?.match(/^.+-(\d{4})03$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNumber) maxNumber = num;
      }
    });
    
      const nextNumber = (maxNumber + 1).toString().padStart(4, '0');
      const generatedCode = `${company.company_code}-${nextNumber}03`;
      return generatedCode;
    }
  };

  // Hàm refresh mã tài sản
  const handleRefreshAssetCode = async () => {
    const companyId = form.getFieldValue('company_id');
    if (companyId) {
      const newAssetCode = await generateAssetCode(companyId);
      form.setFieldsValue({ asset_code: newAssetCode });
      setAssetCodeError(null);
    }
  };

  // Chuẩn hóa initialValues nếu có (copy)
  const getInitialValues = () => {
    if (!initialValues) return undefined;
    const iv = { ...initialValues };
    // Map ngày tháng sang dayjs
    if (iv.purchase_date) iv.purchase_date = dayjs(iv.purchase_date);
    if (iv.warranty_expiry_date) iv.warranty_expiry_date = dayjs(iv.warranty_expiry_date);
    if (iv.last_audit_date) iv.last_audit_date = dayjs(iv.last_audit_date);
    // Map technical_specifications object -> array đúng thứ tự
    if (iv.technical_specifications && typeof iv.technical_specifications === 'object' && !Array.isArray(iv.technical_specifications)) {
      const specObj = iv.technical_specifications;
      const defaultLabels = ['Thương hiệu', 'Model', 'Thông tin mô tả', 'Tiêu thụ', 'Năng suất'];
      const arr = defaultLabels.map(label => ({ key: label, value: specObj[label] ?? '' }));
      // Thêm các trường khác ngoài 5 nhãn mặc định
      Object.entries(specObj).forEach(([key, value]) => {
        if (!defaultLabels.includes(key)) arr.push({ key, value });
      });
      iv.technical_specifications = arr;
    }
    // Xóa các trường không cần copy
    delete iv.id;
    delete iv.asset_code;
    delete iv.attachments;
    delete iv.created_at;
    delete iv.updated_at;
    delete iv.images;
    delete iv.documents;
    return iv;
  };

  // Hàm kiểm tra trùng mã tài sản
  const checkDuplicateAssetCode = React.useCallback(
    debounce(async (code: string, companyId: number) => {
      if (!code || !companyId) {
        setAssetCodeError(null);
        return;
      }

      setCheckingAssetCode(true);
      try {
        const res = await apiClient.get(`/assets/check-duplicate-code?company_id=${companyId}&asset_code=${encodeURIComponent(code)}`) as { exists: boolean };

        if (res.exists) {
          setAssetCodeError('Mã tài sản đã tồn tại trong công ty này');
        } else {
          setAssetCodeError(null);
        }
      } catch (error) {
        setAssetCodeError(null);
      }
      setCheckingAssetCode(false);
    }, 400),
    []
  );

  // Khi là admin hoặc manager thì set công ty mặc định và sinh mã
  React.useEffect(() => {
    if (!isSuperAdmin && user?.company_id) {
      form.setFieldsValue({ company_id: user.company_id });
      // Tự động sinh mã tài sản khi set công ty mặc định
      generateAssetCode(user.company_id).then(assetCode => {
      form.setFieldsValue({ asset_code: assetCode });
      });
    }
  }, [isSuperAdmin, user, form, companyList]);

  // Lấy danh sách loại bảo dưỡng khi mở form
  React.useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/maintenance-types`);
        setMaintenanceTypes(res.data);
      } catch {}
    })();
  }, []);

  // Đảm bảo mã tài sản được tạo khi form được khởi tạo
  React.useEffect(() => {
    const companyId = form.getFieldValue('company_id');
    if (companyId && !form.getFieldValue('asset_code')) {
      generateAssetCode(companyId).then(assetCode => {
      form.setFieldsValue({ asset_code: assetCode });
      });
    }
  }, [form, companyList]);

  // Đảm bảo mã tài sản được tạo khi user thay đổi
  React.useEffect(() => {
    if (!isSuperAdmin && user?.company_id && companyList.length > 0) {
      generateAssetCode(user.company_id).then(assetCode => {
      form.setFieldsValue({ 
        company_id: user.company_id,
        asset_code: assetCode 
        });
      });
    }
  }, [user, companyList, isSuperAdmin, form]);

  // Đảm bảo mã được tạo khi form mount và data đã sẵn sàng
  React.useEffect(() => {
    const companyId = form.getFieldValue('company_id');
    const assetCode = form.getFieldValue('asset_code');
    
    if (companyId && !assetCode && companyList.length > 0) {
      generateAssetCode(companyId).then(newAssetCode => {
      form.setFieldsValue({ asset_code: newAssetCode });
      });
    }
  }, [companyList, form]);

  // Khi chọn công ty thì tự sinh mã tài sản và reset lỗi mã
  const handleCompanyChange = async (companyId: number) => {
    const assetCode = await generateAssetCode(companyId);
    form.setFieldsValue({ asset_code: assetCode });
    setAssetCodeError(null);
    // Lọc lại danh sách vị trí theo công ty
    const filteredLocation = locationList.filter(l => l.company_id === companyId);
    setCurrentLocationList(filteredLocation);
    // Lọc lại danh sách nhân viên theo công ty
    const filteredEmployee = employeeList.filter(e => e.company_id === companyId);
    setCurrentEmployeeList(filteredEmployee);
    // Reset vị trí và nhân viên nếu không còn phù hợp
    form.setFieldsValue({ location_id: undefined, assigned_to_employee_id: undefined });
  };

  // Xử lý khi tạo nhà cung cấp mới thành công
  const handleSupplierCreated = (newSupplier: Supplier) => {
    setCurrentSupplierList(prev => [...prev, newSupplier]);
    form.setFieldsValue({ supplier_id: newSupplier.id });
    setSupplierModalVisible(false);
    // Gọi callback để cập nhật danh sách nhà cung cấp ở component cha
    if (onSupplierCreated) {
      onSupplierCreated();
    }
  };

  // Xử lý khi tạo vị trí mới thành công
  const handleLocationCreated = (newLocation: any) => {
    setCurrentLocationList(prev => [...prev, newLocation]);
    form.setFieldsValue({ location_id: newLocation.id });
    setLocationModalVisible(false);
    // Gọi callback để cập nhật danh sách vị trí ở component cha
    if (onLocationCreated) {
      onLocationCreated();
    }
  };

  // Xử lý khi tạo loại tài sản mới thành công
  const handleCategoryCreated = (newCategory: any) => {
    setCurrentCategoryList(prev => [...prev, newCategory]);
    form.setFieldsValue({ category_id: newCategory.id });
    setCategoryModalVisible(false);
    // Gọi callback để cập nhật danh sách loại tài sản ở component cha
    if (onCategoryCreated) {
      onCategoryCreated();
    }
  };

  // Xử lý khi tạo nhân viên mới thành công
  const handleEmployeeCreated = (newEmployee: Employee) => {
    setCurrentEmployeeList(prev => [...prev, newEmployee]);
    form.setFieldsValue({ assigned_to_employee_id: newEmployee.id });
    setEmployeeModalVisible(false);
    // Gọi callback để cập nhật danh sách nhân viên ở component cha
    if (onEmployeeCreated) {
      onEmployeeCreated();
    }
  };

  // Xử lý upload ảnh instant
  const handleImageUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append('file', file as Blob);
    if (form.getFieldValue('company_id')) formData.append('company_id', form.getFieldValue('company_id'));
    if (form.getFieldValue('asset_code')) formData.append('asset_code', form.getFieldValue('asset_code'));
    if (user?.id) formData.append('uploaded_by_user_id', String(user.id));
    
    // Lấy token từ localStorage
    const token = localStorage.getItem('authToken');
    const headers: any = { 'Content-Type': 'multipart/form-data' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    try {
      const res = await axios.post(`${API_URL}/asset-attachments/upload`, formData, {
        headers,
        onUploadProgress: (event) => {
          if (event.total) {
            onProgress?.({ percent: Math.round((event.loaded / event.total) * 100) });
          }
        },
      });
      // Cập nhật uploadedImages
      setUploadedImages(prev => {
        const newState = [...prev, res.data];
        return newState;
      });
      // Cập nhật fileList của Upload (gán url và name)
      onSuccess?.({
        ...res.data,
        url: res.data.file_path,
        name: res.data.file_name,
        response: res.data
      }, file);
    } catch (err) {
      onError?.(err as any);
    }
  };

  // Xử lý upload tài liệu instant
  const handleDocUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append('file', file as Blob);
    if (form.getFieldValue('company_id')) formData.append('company_id', form.getFieldValue('company_id'));
    if (form.getFieldValue('asset_code')) formData.append('asset_code', form.getFieldValue('asset_code'));
    if (user?.id) formData.append('uploaded_by_user_id', String(user.id));
    
    // Lấy token từ localStorage
    const token = localStorage.getItem('authToken');
    const headers: any = { 'Content-Type': 'multipart/form-data' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    try {
      const res = await axios.post(`${API_URL}/asset-attachments/upload`, formData, {
        headers,
        onUploadProgress: (event) => {
          if (event.total) {
            onProgress?.({ percent: Math.round((event.loaded / event.total) * 100) });
          }
        },
      });
      setUploadedDocs(prev => {
        const newState = [...prev, res.data];
        return newState;
      });
      onSuccess?.({
        ...res.data,
        url: res.data.file_path,
        name: res.data.file_name,
        response: res.data
      }, file);
    } catch (err) {
      onError?.(err as any);
    }
  };

  // Xử lý xóa ảnh instant
  const handleRemoveImage = async (file: UploadFile) => {
    const info = uploadedImages.find(
      f => f.file_path === file.response?.file_path || f.file_path === file.url || f.file_name === file.name
    );
    if (info) {
      // Lấy token từ localStorage
      const token = localStorage.getItem('authToken');
      const headers: any = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      if (info.id) {
        await axios.delete(`${API_URL}/asset-attachments/${info.id}`, { headers }).catch(() => null);
      } else {
        await axios.delete(`${API_URL}/asset-attachments/delete-by-path`, {
          headers,
          data: { file_path: info.file_path }
        }).catch(() => null);
      }
      setUploadedImages(prev => prev.filter(f => f.file_path !== info.file_path));
    }
    return true;
  };

  // Xử lý xóa tài liệu instant
  const handleRemoveDoc = async (file: UploadFile) => {
    const info = uploadedDocs.find(
      f => f.file_path === file.response?.file_path || f.file_path === file.url || f.file_name === file.name
    );
    if (info) {
      // Lấy token từ localStorage
      const token = localStorage.getItem('authToken');
      const headers: any = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      if (info.id) {
        await axios.delete(`${API_URL}/asset-attachments/${info.id}`, { headers }).catch(() => null);
      } else {
        await axios.delete(`${API_URL}/asset-attachments/delete-by-path`, {
          headers,
          data: { file_path: info.file_path }
        }).catch(() => null);
      }
      setUploadedDocs(prev => prev.filter(f => f.file_path !== info.file_path));
    }
    return true;
  };

  // Xử lý submit
  const handleFinish = async (values: any) => {
    setLoading(true);
    try {
      const maintenance_schedules = selectedMaintenanceTypes.map(typeId => ({
        maintenance_type_id: typeId,
        cycle_months: maintenanceCycles[String(typeId)]
      }));
      // Validate trước khi submit
      if (maintenance_schedules.some(ms => !ms.cycle_months || ms.cycle_months < 1)) {
        message.error('Vui lòng nhập chu kỳ hợp lệ cho tất cả loại bảo dưỡng!');
        setLoading(false);
        return;
      }
      const techSpecsArr = values.technical_specifications || [];
      const techSpecsObj = processTechnicalSpecifications(techSpecsArr);
      const payload = {
        ...values,
        technical_specifications: techSpecsObj,
        last_audit_date: dayjs().format('YYYY-MM-DD'),
        public_uuid: uuidv4(),
        asset_approval_status: 'approved',
        images: uploadedImages.map(f => f.file_path),
        documents: uploadedDocs.map(f => f.file_path),
        maintenance_schedules,
        accessories: hasAccessories ? accessoriesDraft.map(acc => ({
          name: acc.name,
          quantity: acc.quantity,
          price: acc.price,
          total_price: acc.total_price,
          unit: acc.unit,
          purchase_date: acc.purchase_date,
          purchase_document: acc.purchase_document,
          notes: acc.notes,
        })) : [],
      };
      
      // 1. Gọi API tạo asset
      const asset = await apiClient.createAsset(payload) as Asset;
      // 2. Gán asset_id cho các file vừa upload
      if (asset && asset.id) {
        // Lấy token từ localStorage
        const token = localStorage.getItem('authToken');
        const headers: any = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        for (const file of uploadedImages) {
          await axios.patch(`${API_URL}/asset-attachments/update-asset-id`, {
            file_name: file.file_name,
            asset_id: asset.id,
          }, { headers });
        }
        for (const file of uploadedDocs) {
          await axios.patch(`${API_URL}/asset-attachments/update-asset-id`, {
            file_name: file.file_name,
            asset_id: asset.id,
          }, { headers });
        }
      }
      
      message.success('Tạo tài sản thành công!');
      onSuccess();
      form.resetFields();
      setImageFiles([]);
      setDocFiles([]);
      setUploadedImages([]);
      setUploadedDocs([]);
      setAccessoriesDraft([]);
      setAccessoriesKey(prev => prev + 1);
    } catch (err) {
      message.error('Tạo tài sản thất bại!');
    } finally {
      setLoading(false);
    }
  };

  // Hàm hủy form: xóa file đã upload instant
  const handleCancel = async () => {
    // Lấy token từ localStorage
    const token = localStorage.getItem('authToken');
    const headers: any = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    // Xóa ảnh đã upload
    await Promise.all(uploadedImages.map(async (f) => {
      if (f.id) {
        // Có bản ghi DB, xóa qua API
        await axios.delete(`${API_URL}/asset-attachments/${f.id}`, { headers }).catch(() => null);
      } else {
        // Chưa có bản ghi DB, xóa file vật lý trực tiếp
        await axios.delete(`${API_URL}/asset-attachments/delete-by-path`, {
          headers,
          data: { file_path: f.file_path }
        }).catch(() => null);
      }
    }));
    setUploadedImages([]);
    setImageFiles([]);
    
    // Xóa tài liệu đã upload
    await Promise.all(uploadedDocs.map(async (f) => {
      if (f.id) {
        // Có bản ghi DB, xóa qua API
        await axios.delete(`${API_URL}/asset-attachments/${f.id}`, { headers }).catch(() => null);
      } else {
        // Chưa có bản ghi DB, xóa file vật lý trực tiếp
        await axios.delete(`${API_URL}/asset-attachments/delete-by-path`, {
          headers,
          data: { file_path: f.file_path }
        }).catch(() => null);
      }
    }));
    setUploadedDocs([]);
    setDocFiles([]);
    
    // Gọi callback onCancel để đóng form/modal
    onCancel();
    form.resetFields();
    form.setFieldsValue({ technical_specifications: defaultTechFields });
    setAccessoriesDraft([]);
    setAccessoriesKey(prev => prev + 1);
    // Force re-render component
    setAccessoriesKey(prev => prev + 1);
  };

  const defaultTechFields = [
    { key: 'Thương hiệu', value: '' },
    { key: 'Model', value: '' },
    { key: 'Thông tin mô tả', value: '' },
    { key: 'Tiêu thụ', value: '0' },
    { key: 'Năng suất', value: '0' },
  ];

  React.useEffect(() => {
    if (initialValues) {
      // Fill maintenance_schedules
      if (Array.isArray(initialValues.maintenance_schedules)) {
        setSelectedMaintenanceTypes(initialValues.maintenance_schedules.map((ms: any) => ms.maintenance_type_id));
        const cycles: Record<string, number | undefined> = {};
        initialValues.maintenance_schedules.forEach((ms: any) => {
          cycles[String(ms.maintenance_type_id)] = ms.cycle_months;
        });
        setMaintenanceCycles(cycles);
      }
      // Sinh mã tài sản mới nếu có company_id
      if (initialValues.company_id) {
        generateAssetCode(initialValues.company_id).then(code => {
        form.setFieldsValue({ asset_code: code });
        });
      }
    }
  }, [initialValues]);

  // Gộp logic fill bảo dưỡng khi copy vào useEffect duy nhất
  React.useEffect(() => {
    if (
      initialValues &&
      Array.isArray(initialValues.maintenance_schedules) &&
      maintenanceTypes.length > 0
    ) {
      setSelectedMaintenanceTypes(initialValues.maintenance_schedules.map((ms: any) => ms.maintenance_type_id));
      const cycles: Record<string, number | undefined> = {};
      initialValues.maintenance_schedules.forEach((ms: any) => {
        cycles[String(ms.maintenance_type_id)] = ms.cycle_months;
      });
      setMaintenanceCycles(cycles);
    } else if (!initialValues) {
      setSelectedMaintenanceTypes([]);
      setMaintenanceCycles({});
    }
  }, [initialValues, maintenanceTypes]);

  // Khi mount, nếu có sẵn company_id thì lọc luôn
  React.useEffect(() => {
    const companyId = form.getFieldValue('company_id') || (user?.role !== 'super_admin' ? user?.company_id : undefined);
    if (companyId) {
      setCurrentLocationList(filterListByCompany(locationList, companyId));
      setCurrentEmployeeList(filterListByCompany(employeeList, companyId));
    }
  }, [locationList, employeeList, user]);

  return (
    <Spin spinning={loading || authLoading} tip="Đang xử lý...">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onFinishFailed={({ errorFields }) => {
          if (!errorFields || errorFields.length === 0) return;
          const field = errorFields[0]?.name?.[0];
          const tabMap = getTabMapForValidation();
          setActiveTabKey(tabMap[String(field)] || '1');
        }}
        initialValues={getInitialValues() || {
          technical_specifications: defaultTechFields,
          ...(isCompanyAdminOrManager && user?.company_id ? { 
            company_id: user.company_id,
            asset_code: '' // Sẽ được set bởi useEffect
          } : {})
        }}
      >
        <Tabs 
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          defaultActiveKey="1"
          type="card"
          size="large"
          style={{ 
            backgroundColor: 'transparent',
            padding: '0'
          }}
          tabBarStyle={{
            marginBottom: '24px',
            borderBottom: '2px solid #f0f0f0'
          }}
          tabBarGutter={8}
        >
          <Tabs.TabPane 
            tab={
              <span style={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: '500',
                fontSize: '14px'
              }}>
                <InfoCircleOutlined style={{ marginRight: 8, fontSize: '16px' }} />
                Thông tin chung
              </span>
            } 
            key="1"
          >
            <Row gutter={24}>
              <Col span={24}>
                <Card 
                  title={
                    <span>
                      <InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                      Thông tin cơ bản
                    </span>
                  }
                  size="small"
                  style={{ 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}
                  headStyle={{
                    backgroundColor: '#fafafa',
                    borderBottom: '1px solid #f0f0f0',
                    fontWeight: '600'
                  }}
                  bodyStyle={{
                    padding: '24px'
                  }}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="company_id" 
                        label="Công ty" 
                        rules={[{ required: true, message: 'Bắt buộc' }]}
                        style={{ marginBottom: '16px' }}
                      > 
                        <Select
                          placeholder="Chọn công ty"
                          onChange={handleCompanyChange}
                          disabled={!isSuperAdmin}
                        >
                          {companyList.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="asset_code" 
                        label="Mã tài sản" 
                        rules={[
                          { required: true, message: 'Bắt buộc' },
                          () => ({
                            validator(_, value) {
                              if (!value || !assetCodeError) return Promise.resolve();
                              return Promise.reject(new Error(assetCodeError));
                            }
                          })
                        ]}
                        validateStatus={assetCodeError ? 'error' : checkingAssetCode ? 'validating' : undefined}
                        help={assetCodeError}
                        style={{ marginBottom: '16px' }}
                      > 
                        <Input 
                          disabled={!isSuperAdmin}
                          addonAfter={
                            <Button 
                              icon={<ReloadOutlined />} 
                              onClick={handleRefreshAssetCode}
                              disabled={!isSuperAdmin}
                              size="small"
                              type="text"
                              title="Tạo lại mã tài sản tự động"
                            />
                          }
                          onChange={e => {
                            const code = e.target.value;
                            const companyId = form.getFieldValue('company_id');
                            checkDuplicateAssetCode(code, companyId);
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="name" 
                        label="Tên tài sản" 
                        rules={[{ required: true, message: 'Bắt buộc' }]}
                        style={{ marginBottom: '16px' }}
                      > 
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="category_id" 
                        label="Loại tài sản" 
                        rules={[{ required: true, message: 'Bắt buộc' }]}
                        style={{ marginBottom: '16px' }}
                      > 
                        <Select 
                          placeholder="Chọn loại tài sản" 
                          allowClear 
                          showSearch 
                          optionFilterProp="children"
                          dropdownRender={(menu) => (
                            <div>
                              {menu}
                              <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                                <Button
                                  type="text"
                                  icon={<span>+</span>}
                                  onClick={() => setCategoryModalVisible(true)}
                                  style={{ width: '100%', textAlign: 'left' }}
                                >
                                  + Tạo loại tài sản mới
                                </Button>
                              </div>
                            </div>
                          )}
                        >
                          {currentCategoryList.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="status" 
                        label="Trạng thái" 
                        rules={[{ required: true, message: 'Bắt buộc' }]}
                        style={{ marginBottom: '16px' }}
                      > 
                        <Select placeholder="Chọn trạng thái...">
                          <Select.Option value="pending_deployment">Chờ triển khai</Select.Option>
                          <Select.Option value="in_use">Đang sử dụng</Select.Option>
                          <Select.Option value="in_storage">Lưu kho</Select.Option>
                          <Select.Option value="under_maintenance">Bảo trì/Sửa chữa</Select.Option>
                          <Select.Option value="disposed">Đã thanh lý</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="location_id" 
                        label="Vị trí" 
                        rules={[{ required: true, message: 'Bắt buộc' }]}
                        style={{ marginBottom: '16px' }}
                      > 
                        <Select 
                          placeholder="Chọn vị trí" 
                          allowClear 
                          showSearch 
                          optionFilterProp="children"
                          dropdownRender={(menu) => (
                            <div>
                              {menu}
                              <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                                <Button
                                  type="text"
                                  icon={<span>+</span>}
                                  onClick={() => setLocationModalVisible(true)}
                                  style={{ width: '100%', textAlign: 'left' }}
                                >
                                  + Tạo vị trí mới
                                </Button>
                              </div>
                            </div>
                          )}
                        >
                          {currentLocationList.map(l => <Select.Option key={l.id} value={l.id}>{l.name}</Select.Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="technical_condition" 
                        label="Trạng thái kỹ thuật"
                        style={{ marginBottom: '16px' }}
                      >
                        <Select placeholder="Chọn tình trạng">
                          <Select.Option value="new">Mới</Select.Option>
                          <Select.Option value="good">Tốt</Select.Option>
                          <Select.Option value="average">Trung bình</Select.Option>
                          <Select.Option value="bad">Kém</Select.Option>
                          <Select.Option value="broken">Hỏng</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="parent_asset_id" 
                        label="Tài sản cha"
                        style={{ marginBottom: '16px' }}
                      >
                        <Select placeholder="Chọn tài sản cha" allowClear showSearch optionFilterProp="children">
                          {assetList.map(asset => (
                            <Select.Option key={asset.id} value={asset.id}>
                              {asset.asset_code} - {asset.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item 
                        name="description" 
                        label="Mô tả"
                        style={{ marginBottom: '16px' }}
                      >
                        <Input.TextArea rows={4} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
            <Row gutter={24} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card 
                  title={
                    <span>
                      <ShopOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                      Thông tin khác
                    </span>
                  }
                  size="small"
                  style={{ 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                    borderRadius: '8px'
                  }}
                  headStyle={{
                    backgroundColor: '#fafafa',
                    borderBottom: '1px solid #f0f0f0',
                    fontWeight: '600'
                  }}
                  bodyStyle={{
                    padding: '24px'
                  }}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="assigned_to_employee_id" 
                        label="Nhân viên được giao"
                        style={{ marginBottom: '16px' }}
                      >
                        <Select 
                          placeholder="Chọn nhân viên" 
                          allowClear 
                          showSearch 
                          optionFilterProp="children"
                          dropdownRender={(menu) => (
                            <div>
                              {menu}
                              <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                                <Button
                                  type="text"
                                  icon={<span>+</span>}
                                  onClick={() => setEmployeeModalVisible(true)}
                                  style={{ width: '100%', textAlign: 'left' }}
                                >
                                  + Tạo nhân viên mới
                                </Button>
                              </div>
                            </div>
                          )}
                        >
                          {currentEmployeeList.map(emp => (
                            <Select.Option key={emp.id} value={emp.id}>
                              {emp.full_name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="supplier_id" 
                        label="Nhà cung cấp"
                        style={{ marginBottom: '16px' }}
                      >
                        <Select 
                          placeholder="Chọn nhà cung cấp" 
                          allowClear 
                          showSearch 
                          optionFilterProp="children"
                          dropdownRender={(menu) => (
                            <div>
                              {menu}
                              <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                                <Button
                                  type="text"
                                  icon={<span>+</span>}
                                  onClick={() => setSupplierModalVisible(true)}
                                  style={{ width: '100%', textAlign: 'left' }}
                                >
                                  + Tạo nhà cung cấp mới
                                </Button>
                              </div>
                            </div>
                          )}
                        >
                          {currentSupplierList.map(sup => (
                            <Select.Option key={sup.id} value={sup.id}>
                              {sup.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="purchase_cost" 
                        label="Giá mua"
                        style={{ marginBottom: '16px' }}
                        extra={accessoriesDraft.length > 0 ? "Giá trị được tính tự động từ tổng giá trị vật tư/thiết bị kèm theo" : ""}
                      >
                        <InputNumber 
                          style={{ width: '100%' }} 
                          min={0}
                          placeholder="Nhập giá mua"
                          formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                          parser={(value: any) => value ? value.replace(/\D/g, '') : undefined}
                          disabled={accessoriesDraft.length > 0}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="purchase_date" 
                        label="Ngày mua"
                        style={{ marginBottom: '16px' }}
                      >
                        <DatePicker 
                          style={{ width: '100%' }} 
                          format="DD/MM/YYYY" 
                          onChange={handlePurchaseDateChange}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="warranty_period_months" 
                        label="Thời hạn bảo hành (tháng)"
                        style={{ marginBottom: '16px' }}
                      >
                        <InputNumber 
                          style={{ width: '100%' }} 
                          min={0} 
                          onChange={handleWarrantyPeriodChange}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="warranty_expiry_date" 
                        label="Ngày hết hạn bảo hành"
                        style={{ marginBottom: '16px' }}
                      >
                        <DatePicker 
                          style={{ width: '100%' }} 
                          format="DD/MM/YYYY" 
                          disabled 
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="last_audit_date" 
                        label="Ngày kiểm kê gần nhất" 
                        initialValue={dayjs()}
                        style={{ marginBottom: '16px' }}
                      >
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabled />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item 
                        name="audit_cycle_months" 
                        label="Chu kỳ kiểm kê (tháng)"
                        style={{ marginBottom: '16px' }}
                      >
                        <InputNumber style={{ width: '100%' }} min={0} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="has_accessories" valuePropName="checked">
                    <Checkbox checked={hasAccessories} onChange={e => setHasAccessories(e.target.checked)}>
                      Tài sản có vật tư/thiết bị kèm theo
                    </Checkbox>
                  </Form.Item>

                </Card>
              </Col>
            </Row>
          </Tabs.TabPane>
          <Tabs.TabPane 
            tab={
              <span style={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: '500',
                fontSize: '14px'
              }}>
                <SettingOutlined style={{ marginRight: 8, fontSize: '16px' }} />
                Thông số kỹ thuật
              </span>
            } 
            key="2"
          >
            <Form.List name="technical_specifications">
              {(fields) => {
                const defaultLabels = [
                  'Thương hiệu',
                  'Model',
                  'Thông tin mô tả',
                  'Tiêu thụ',
                  'Năng suất',
                ];
                return (
                  <div>
                    {fields.slice(0, 5).map(({ key, name, ...restField }, idx) => (
                      <Form.Item
                        key={key}
                        label={defaultLabels[idx]}
                        name={[name, 'value']}
                        rules={[
                          (idx === 3 || idx === 4) ? { pattern: /^\d*$/, message: 'Chỉ nhập số!' } : {},
                        ]}
                        style={{ marginBottom: 12 }}
                      >
                        {idx === 2 ? (
                          <Input.TextArea
                            placeholder={defaultLabels[idx]}
                            rows={3}
                          />
                        ) : (
                        <Input
                          placeholder={defaultLabels[idx]}
                          type={idx === 3 || idx === 4 ? 'number' : 'text'}
                          min={idx === 3 || idx === 4 ? 0 : undefined}
                        />
                        )}
                      </Form.Item>
                    ))}
                  </div>
                );
              }}
            </Form.List>
          </Tabs.TabPane>
          <Tabs.TabPane 
            tab={
              <span style={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: '500',
                fontSize: '14px'
              }}>
                <ToolOutlined style={{ marginRight: 8, fontSize: '16px' }} />
                Bảo dưỡng
              </span>
            } 
            key="3"
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} lg={12}>
                <Card 
                  title="Loại bảo dưỡng"
                  size="small"
                  style={{ 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                    borderRadius: '8px'
                  }}
                  headStyle={{
                    backgroundColor: '#fafafa',
                    borderBottom: '1px solid #f0f0f0',
                    fontWeight: '600'
                  }}
                  bodyStyle={{
                    padding: '16px'
                  }}
                >
                  <Form.Item label="Chọn loại bảo dưỡng">
                    <Select
                      mode="multiple"
                      placeholder="Chọn loại bảo dưỡng"
                      value={selectedMaintenanceTypes}
                      onChange={value => {
                        setSelectedMaintenanceTypes(value);
                        // Xóa chu kỳ của loại đã bỏ chọn
                        setMaintenanceCycles(prev => {
                          const newCycles = { ...prev };
                          Object.keys(newCycles).forEach((k) => {
                            const keyNum = parseInt(k, 10);
                            if (!value.includes(keyNum)) {
                              delete newCycles[k];
                            }
                          });
                          return newCycles;
                        });
                      }}
                      style={{ width: '100%' }}
                      optionFilterProp="children"
                      showSearch
                    >
                      {maintenanceTypes.map(mt => (
                        <Select.Option key={mt.id} value={mt.id}>{mt.name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card 
                  title="Chu kỳ bảo dưỡng"
                  size="small"
                  style={{ 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                    borderRadius: '8px'
                  }}
                  headStyle={{
                    backgroundColor: '#fafafa',
                    borderBottom: '1px solid #f0f0f0',
                    fontWeight: '600'
                  }}
                  bodyStyle={{
                    padding: '16px'
                  }}
                >
                  {selectedMaintenanceTypes.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      color: '#999', 
                      padding: '20px',
                      fontStyle: 'italic'
                    }}>
                      Vui lòng chọn loại bảo dưỡng ở cột bên trái
                    </div>
                  ) : (
                    selectedMaintenanceTypes.map(typeId => {
                      const mt = maintenanceTypes.find(mt => mt.id === typeId);
                      return (
                        <Form.Item
                          key={typeId}
                          label={`${mt?.name || 'Loại bảo dưỡng'}`}
                          required
                          validateStatus={
                            maintenanceCycles[String(typeId)] && maintenanceCycles[String(typeId)]! > 0 ? undefined : 'error'
                          }
                          help={
                            maintenanceCycles[String(typeId)] && maintenanceCycles[String(typeId)]! > 0
                              ? ''
                              : 'Vui lòng nhập chu kỳ (>=1 tháng)'
                          }
                          style={{ marginBottom: '12px' }}
                        >
                          <Input
                            type="number"
                            min={1}
                            style={{ width: '100%' }}
                            value={maintenanceCycles[String(typeId)] || ''}
                            onChange={e => {
                              const v = parseInt(e.target.value, 10);
                              setMaintenanceCycles(prev => ({ ...prev, [typeId]: isNaN(v) ? undefined : v }));
                            }}
                            placeholder="Nhập chu kỳ (tháng)"
                            addonAfter="tháng"
                          />
                        </Form.Item>
                      );
                    })
                  )}
                </Card>
              </Col>
            </Row>
          </Tabs.TabPane>
          <Tabs.TabPane 
            tab={
              <span style={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: '500',
                fontSize: '14px'
              }}>
                <PaperClipOutlined style={{ marginRight: 8, fontSize: '16px' }} />
                Hình ảnh & hồ sơ
              </span>
            } 
            key="4"
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} lg={12}>
                <Form.Item label="Ảnh tài sản">
                  <Upload
                    customRequest={handleImageUpload}
                    onRemove={handleRemoveImage}
                    fileList={imageFiles}
                    onChange={({ fileList }) => setImageFiles(fileList)}
                    listType="picture"
                    maxCount={5}
                    accept="image/*"
                  >
                    <Button>Chọn ảnh</Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item label="Tài liệu đính kèm">
                  <Upload
                    customRequest={handleDocUpload}
                    onRemove={handleRemoveDoc}
                    fileList={docFiles}
                    onChange={({ fileList }) => setDocFiles(fileList)}
                    maxCount={5}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  >
                    <Button>Chọn file</Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
          </Tabs.TabPane>
          {hasAccessories && (
            <Tabs.TabPane 
              tab={<span><AppstoreAddOutlined style={{ marginRight: 8, fontSize: '16px' }} />Vật tư / Thiết bị</span>}
              key="5"
            >
              <div>
                <AssetAccessoriesDraft key={`accessories-${accessoriesKey}`} onChange={(accessories) => {
                  setAccessoriesDraft(accessories);
                  updateAssetValue(accessories);
                }} />
                {accessoriesDraft.length > 0 && (
                  <div style={{ 
                    marginTop: 16, 
                    padding: 12, 
                    backgroundColor: '#f6ffed', 
                    border: '1px solid #b7eb8f', 
                    borderRadius: 6,
                    textAlign: 'center'
                  }}>
                    <strong>Tổng giá trị vật tư/thiết bị: {calculateTotalAccessoriesValue(accessoriesDraft).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} VNĐ</strong>
                  </div>
                )}
              </div>
            </Tabs.TabPane>
          )}
        </Tabs>
        <Form.Item style={{ textAlign: 'right', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
              <Button onClick={handleCancel}>Hủy</Button>
              <Button type="primary" htmlType="submit">Tạo mới</Button>
            </div>
          </div>
        </Form.Item>
      </Form>
      
      {/* Modal tạo nhà cung cấp mới */}
      <SupplierModal
        visible={supplierModalVisible}
        onCancel={() => setSupplierModalVisible(false)}
        onSuccess={handleSupplierCreated}
      />
      
      {/* Modal tạo vị trí mới */}
      <LocationModal
        visible={locationModalVisible}
        onCancel={() => setLocationModalVisible(false)}
        onSuccess={handleLocationCreated}
        companies={companyList}
        locations={currentLocationList}
        isSuperAdmin={isSuperAdmin}
        currentUser={user}
      />
      
      {/* Modal tạo loại tài sản mới */}
      <AssetCategoryModal
        visible={categoryModalVisible}
        onCancel={() => setCategoryModalVisible(false)}
        onSuccess={handleCategoryCreated}
      />
      
      {/* Modal tạo nhân viên mới */}
      <EmployeeModal
        visible={employeeModalVisible}
        onCancel={() => setEmployeeModalVisible(false)}
        onSuccess={handleEmployeeCreated}
        companies={companyList}
        isSuperAdmin={isSuperAdmin}
        currentUser={user}
      />
    </Spin>
  );
};

export default AssetCreateForm; 